import React, { useCallback } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useParams } from "react-router-dom";
import { isNil } from "ramda";

import { AffinityForm, ConfirmationView, FormView } from "affinityForm";
import { translateErrorMessages, useDryRunMutation, useSubmitMutation } from "affinityForm/helpers";
import { Layout } from "affinityUI/structures/layout";
import {
  manualResultsValidationSchema,
  ResultsEditConfirmation,
  ResultsEditForm,
} from "components/views/patientDetails/resultEdit";
import { manualResultValidationErrors } from "components/views/patientDetails/resultEdit/resultsEditForm/messages";
import { ResultFormdata } from "components/views/patientDetails/resultEdit/types";
import { CreateResult, CreateResultInput, useGetPatientDetailsQuery } from "gql";
import { collectGarbage, composeUpdates, invalidatePatientDetails, invalidatePatientsList } from "gql/invalidations";
import { dateTimeToTimestamp } from "lib/helpers";

const createFormdataToInput: (data: ResultFormdata) => CreateResultInput = ({
  dateRecorded,
  timeRecorded,
  whiteBloodCells,
  neutrophils,
  haemoglobin,
  platelets,
  ...restFields
}) => {
  return {
    ...restFields,
    whiteBloodCells: { ...whiteBloodCells, unit: "10^9/L" },
    neutrophils: { ...neutrophils, unit: "10^9/L" },
    platelets: { ...platelets, unit: "10^9/L" },
    haemoglobin: { ...haemoglobin, unit: "g/L" },
    dateRecorded: dateTimeToTimestamp({ dateRecorded, timeRecorded }),
  };
};

export const AddResultsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { showBoundary: onError } = useErrorBoundary();

  const { loading, data } = useGetPatientDetailsQuery({ onError, variables: { id: id as string }, errorPolicy: "all" });
  const patient = data?.patient;
  const update = composeUpdates([invalidatePatientsList, invalidatePatientDetails, collectGarbage]);
  const resultVarSetter = useCallback(
    (formData: ResultFormdata) => {
      const result = createFormdataToInput(formData);
      return {
        patientId: patient?.id as string,
        result,
      };
    },
    [patient]
  );

  const validateResult = useDryRunMutation(CreateResult, {
    varSetter: resultVarSetter,
    dataGetter: useCallback((response) => response?.data?.createResult, []),
    parseError: translateErrorMessages(manualResultValidationErrors),
  });

  const createResult = useSubmitMutation(CreateResult, {
    varSetter: resultVarSetter,
    dataGetter: useCallback((response) => response?.data?.createResult, []),
    onSave: `/patients/${patient?.id}`,
    update,
  });

  const formTitle = patient ? `Add result for ${patient.firstName} ${patient.lastName}` : "";
  const confirmationTitle = patient ? `Confirm and Save Result for ${patient.firstName} ${patient.lastName}` : "";

  return (
    <AffinityForm<ResultFormdata>
      asyncValidation={validateResult}
      validationSchema={manualResultsValidationSchema}
      confirmHandler={createResult}
    >
      <FormView>
        <Layout title={formTitle} loading={loading} showBackBtn>
          {!isNil(patient) && <ResultsEditForm patient={patient} />}
        </Layout>
      </FormView>
      <ConfirmationView>
        <Layout title={confirmationTitle} loading={loading} backLink=".." showBackBtn>
          {!isNil(patient) && <ResultsEditConfirmation patient={patient} />}
        </Layout>
      </ConfirmationView>
    </AffinityForm>
  );
};
